import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { usersApi } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import {
  UserGroupIcon,
  CheckIcon,
  XMarkIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

interface PendingUser {
  id: number
  firstName: string
  lastName: string
  email: string
  phone?: string
  dateOfBirth?: string
  role: 'church_coordinator' | 'judge' | 'participant'
  churchId?: number
  status: 'pending'
  createdAt: string
}

const UserApproval: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState<'all' | 'church_coordinator' | 'judge'>('all')
  const [rejectingUser, setRejectingUser] = useState<PendingUser | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')

  const queryClient = useQueryClient()

  const { data: pendingUsers = [], isLoading, error } = useQuery(
    ['pending-users', selectedRole],
    () => usersApi.getUsers({
      endpoint: '/users/pending/all',
      params: { role: selectedRole === 'all' ? undefined : selectedRole }
    }),
    {
      select: (response) => response.data
    }
  )

  const approveMutation = useMutation(
    (userId: number) => fetch(`/api/users/${userId}/approve`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        'Content-Type': 'application/json'
      }
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pending-users')
        queryClient.invalidateQueries('system-stats')
        toast.success('User approved successfully!')
      },
      onError: (_error: any) => {
        toast.error('Failed to approve user')
      }
    }
  )

  const rejectMutation = useMutation(
    ({ userId, reason }: { userId: number; reason: string }) =>
      fetch(`/api/users/${userId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pending-users')
        queryClient.invalidateQueries('system-stats')
        setRejectingUser(null)
        setRejectionReason('')
        toast.success('User rejected successfully!')
      },
      onError: (_error: any) => {
        toast.error('Failed to reject user')
      }
    }
  )

  const handleApprove = (user: PendingUser) => {
    if (window.confirm(`Are you sure you want to approve ${user.firstName} ${user.lastName} as a ${user.role.replace('_', ' ')}?`)) {
      approveMutation.mutate(user.id)
    }
  }

  const handleReject = () => {
    if (rejectingUser && rejectionReason.trim()) {
      rejectMutation.mutate({
        userId: rejectingUser.id,
        reason: rejectionReason.trim()
      })
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'church_coordinator':
        return 'bg-blue-100 text-blue-800'
      case 'judge':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to load pending users</h2>
        <p className="text-gray-600">Please try again later.</p>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">User Approvals</h1>
        <p className="text-gray-600">Review and approve coordinator and judge registrations</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Pending</dt>
                  <dd className="text-lg font-medium text-gray-900">{pendingUsers.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BuildingOfficeIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Coordinators</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {pendingUsers.filter((u: PendingUser) => u.role === 'church_coordinator').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-6 w-6 bg-purple-500 rounded-full"></div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Judges</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {pendingUsers.filter((u: PendingUser) => u.role === 'judge').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex items-center space-x-4">
          <label htmlFor="role-filter" className="text-sm font-medium text-gray-700">
            Filter by Role:
          </label>
          <select
            id="role-filter"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as any)}
            className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Roles</option>
            <option value="church_coordinator">Church Coordinators</option>
            <option value="judge">Judges</option>
          </select>
        </div>
      </div>

      {/* Pending Users List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        {pendingUsers.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pending approvals</h3>
            <p className="mt-1 text-sm text-gray-500">
              All user registrations have been processed.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applied
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingUsers.map((user: PendingUser) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <EnvelopeIcon className="h-4 w-4 mr-1" />
                          {user.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                        {user.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="space-y-1">
                        {user.phone && (
                          <div className="flex items-center">
                            <PhoneIcon className="h-4 w-4 mr-1" />
                            {user.phone}
                          </div>
                        )}
                        {user.dateOfBirth && (
                          <div className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {new Date(user.dateOfBirth).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleApprove(user)}
                          disabled={approveMutation.isLoading}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50"
                          title="Approve"
                        >
                          <CheckIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setRejectingUser(user)}
                          disabled={rejectMutation.isLoading}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          title="Reject"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rejection Modal */}
      {rejectingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">
                  Reject User Registration
                </h3>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                You are about to reject the registration for <strong>{rejectingUser.firstName} {rejectingUser.lastName}</strong>.
                Please provide a reason for the rejection.
              </p>

              <div className="mb-4">
                <label htmlFor="rejection-reason" className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  id="rejection-reason"
                  rows={4}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500"
                  placeholder="Please provide a clear reason for the rejection..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setRejectingUser(null)
                    setRejectionReason('')
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={!rejectionReason.trim() || rejectMutation.isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rejectMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    'Reject User'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserApproval